import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CHAPTERS, MANIFESTO_IMG_1, MACHINERY_1, AERIAL_ROAD, STORMWATER_IMG } from "@/lib/content";

const IMAGES = [STORMWATER_IMG, AERIAL_ROAD, MANIFESTO_IMG_1, MACHINERY_1];
const EASE = [0.22, 1, 0.36, 1];

function Chapter({ chapter, index }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.9", "end 0.1"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["8%", "-8%"]);

  return (
    <div
      ref={ref}
      className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 py-16 md:py-28 border-t border-white/10"
      data-testid={`manifesto-chapter-${chapter.n}`}
    >
      {/* Number + title */}
      <div className="md:col-span-5 lg:col-span-4">
        <div className="sticky top-28">
          <div className="flex items-baseline gap-4">
            <span className="font-display font-black text-[color:var(--te-primary)] text-6xl md:text-8xl leading-none tracking-tighter">
              {chapter.n}
            </span>
            <span className="text-[10px] uppercase tracking-[0.28em] text-white/50 font-display">
              Chapter
            </span>
          </div>
          <motion.h3
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.8, ease: EASE }}
            className="mt-6 font-display font-bold uppercase text-3xl md:text-4xl tracking-tight text-white leading-[0.95]"
          >
            {chapter.title}
          </motion.h3>
        </div>
      </div>

      {/* Body + image */}
      <div className="md:col-span-7 lg:col-span-8 flex flex-col gap-10">
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.9, ease: EASE }}
          className="text-lg md:text-xl font-body text-white/75 max-w-[62ch] leading-relaxed"
        >
          {chapter.body}
        </motion.p>

        {IMAGES[index % IMAGES.length] ? (
          <div className="clip-frame relative w-full max-w-[520px] aspect-[4/5] self-end overflow-hidden">
            <motion.img
              src={IMAGES[index % IMAGES.length]}
              alt="Trilogy Earthworks in progress"
              style={{ y: imgY, filter: "brightness(1.02) contrast(1.06) saturate(1.08)" }}
              className="w-full h-[112%] object-cover scale-105"
              loading="lazy"
            />
            {/* Vignette + bottom fade so photo blends with dark section */}
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "radial-gradient(120% 90% at 50% 30%, transparent 50%, rgba(10,10,10,0.55) 100%)",
              }}
            />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[color:var(--te-bg)] via-[color:var(--te-bg)]/40 to-transparent" />
            {chapter.location ? (
              <div className="absolute bottom-4 left-4 flex items-center gap-2 z-10">
                <span className="w-1.5 h-1.5 bg-[color:var(--te-primary)] rotate-45" />
                <span className="text-[10px] uppercase tracking-[0.28em] text-white font-display">
                  {chapter.location}
                </span>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="relative w-full max-w-[520px] aspect-[4/5] self-end border border-dashed border-white/15 flex items-center justify-center">
            <div className="text-center px-6">
              <div className="w-2 h-2 bg-[color:var(--te-primary)] rotate-45 mx-auto mb-4" />
              <div className="text-[11px] uppercase tracking-[0.28em] text-white/60 font-display mb-2">
                Photo coming
              </div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-white/40 font-display">
                {chapter.location}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Manifesto() {
  return (
    <section
      id="manifesto"
      className="relative bg-[color:var(--te-bg)] px-5 md:px-10 py-24 md:py-32"
      data-testid="manifesto-section"
    >
      <div className="max-w-[1440px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 md:mb-16">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="w-2 h-2 bg-[color:var(--te-primary)] rotate-45" />
              <span className="text-[11px] uppercase tracking-[0.28em] text-[color:var(--te-primary)] font-display">
                The Trilogy way
              </span>
            </div>
            <h2 className="font-display font-black uppercase text-white tracking-tighter leading-[0.9]"
                style={{ fontSize: "clamp(2.4rem, 6vw, 5.5rem)" }}>
              A manifesto,<br />
              <span className="font-serif-italic tracking-normal text-[color:var(--te-primary)]">not a brochure.</span>
            </h2>
          </div>
          <p className="max-w-md text-white/60 font-body text-base md:text-lg leading-relaxed">
            Four chapters on how we approach every job — from the first walk of the block to the final concrete pour.
          </p>
        </div>

        {CHAPTERS.map((c, i) => (
          <Chapter key={c.n} chapter={c} index={i} />
        ))}
      </div>
    </section>
  );
}
